import {
  FileStatusType,
  UPLOAD_COMPLETE_STATES
} from "@/features/createProject/api";
import { PROJECT_FILE_STATUS } from "@/types/project";
import {
  AudiotrackRounded,
  CancelRounded,
  CheckCircle,
  ErrorRounded,
  RefreshRounded,
  VideocamRounded
} from "@mui/icons-material";
import { CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { UppyFile } from "@uppy/core";
import React from "react";

interface FileListItemProps {
  fileKey: string;
  fileName: string;
  fileStatus: FileStatusType;
  onFileRemove?: () => void;
  onFileRetry?: () => void;
  metaData?: UppyFile<any, any>["meta"];
  fileType?: "VIDEO" | "BG_AUDIO";
}

export const FileListItem = ({
  fileKey,
  fileName,
  fileStatus,
  fileType,
  onFileRemove,
  onFileRetry,
  metaData
}: FileListItemProps): React.ReactNode => {
  // const [isRemoved, setIsRemoved] = useState(false);

  // if (isRemoved) {
  //   return null;
  // }

  return (
    <Stack
      key={fileKey}
      px={2}
      py={1}
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"center"}
      maxWidth={"100%"}
      overflow={"hidden"}
      sx={{
        height: "3rem",
        "& .show-on-hover": {
          display: "none"
        },
        "&:hover": [PROJECT_FILE_STATUS.UNSUPPORTED].includes(fileStatus)
          ? {}
          : {
              backgroundColor: (theme) =>
                [PROJECT_FILE_STATUS.FAILED].includes(fileStatus)
                  ? theme.palette.error.hover
                  : theme.palette.action.hover
            },
        "&:hover .hide-on-hover": {
          display: "none"
        },
        "&:hover .show-on-hover": {
          display: "inline-flex"
        }
      }}
    >
      <Stack
        direction={"row"}
        spacing={1}
        alignItems={"center"}
        maxWidth={"90%"}
        flexGrow={1}
      >
        {fileType === "BG_AUDIO" ? (
          <AudiotrackRounded
            sx={{
              height: 24,
              color: (theme) => theme.palette.secondary.focusVisible
            }}
          />
        ) : (
          <VideocamRounded
            sx={{
              height: 24,
              color: (theme) => theme.palette.secondary.focusVisible
            }}
          />
        )}
        <Stack>
          <Typography
            variant="body2"
            textOverflow={"ellipsis"}
            noWrap
            width={"26ch"}
            maxWidth={"100%"}
            color={
              [PROJECT_FILE_STATUS.UNSUPPORTED].includes(fileStatus)
                ? "text.secondary"
                : "text.primary"
            }
          >
            {fileName}
          </Typography>
          {fileStatus === PROJECT_FILE_STATUS.UNSUPPORTED && metaData?.error ? (
            <Typography
              variant="caption"
              textOverflow={"ellipsis"}
              noWrap
              width={"32ch"}
              maxWidth={"100%"}
              color={"error.main"}
            >
              {metaData.error}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
      <Stack direction={"row"} gap={1} alignItems={"center"} flexShrink={0}>
        {UPLOAD_COMPLETE_STATES.includes(fileStatus) ? (
          <IconButton size="small" className="hide-on-hover">
            <CheckCircle color="success" />
          </IconButton>
        ) : null}
        {fileStatus === PROJECT_FILE_STATUS.UPLOADING ? (
          <IconButton size="small" className="hide-on-hover">
            <CircularProgress size={20} />
          </IconButton>
        ) : null}

        {fileStatus === PROJECT_FILE_STATUS.WAITING_FOR_UPLOAD
          ? "Queued for Upload"
          : null}
        {fileStatus === PROJECT_FILE_STATUS.FAILED ? (
          <>
            <IconButton
              size="small"
              onClick={() => {
                if (onFileRetry) {
                  onFileRetry();
                }
              }}
              id={`btn-retry-upload-${fileKey}`}
            >
              <RefreshRounded
                sx={{ color: (theme) => theme.palette.action.active }}
              />
            </IconButton>
            <IconButton size="small" className="hide-on-hover">
              <ErrorRounded color="error" />
            </IconButton>
          </>
        ) : null}
        {fileStatus === PROJECT_FILE_STATUS.UNSUPPORTED ? (
          <>
            <IconButton
              size="small"
              sx={{
                cursor: "default"
              }}
            >
              <ErrorRounded color="error" />
            </IconButton>
          </>
        ) : null}
        {fileStatus !== PROJECT_FILE_STATUS.UNSUPPORTED ? (
          <IconButton
            size="small"
            className="show-on-hover"
            onClick={() => {
              // setIsRemoved(true);
              if (onFileRemove) {
                onFileRemove();
              }
            }}
            id={`btn-remove-file-${fileKey}`}
          >
            <CancelRounded />
          </IconButton>
        ) : null}
      </Stack>
    </Stack>
  );
};
